import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, API } from '@/App';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState({
    type: 'none',
    status: 'inactive',
    isActive: false,
    daysRemaining: 0,
    startDate: null,
    endDate: null,
    isCancelled: false,
    cancellationDate: null,
    loading: true
  });

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/subscription/status`, {
        credentials: 'include',  // Include httpOnly cookies for auth
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription({
          type: data.subscription_type || 'none',
          status: data.subscription_status || 'inactive',
          isActive: data.is_active || false,
          daysRemaining: data.days_remaining || 0,
          startDate: data.subscription_start,
          endDate: data.subscription_end,
          isCancelled: data.is_cancelled || false,
          cancellationDate: data.cancellation_date,
          loading: false
        });
      } else {
        setSubscription(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      setSubscription(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchSubscriptionStatus();
    } else {
      setSubscription({
        type: 'none',
        status: 'inactive',
        isActive: false,
        daysRemaining: 0,
        startDate: null,
        endDate: null,
        isCancelled: false,
        cancellationDate: null,
        loading: false
      });
    }
  }, [user, fetchSubscriptionStatus]);

  const refreshSubscription = useCallback(() => {
    if (user) {
      fetchSubscriptionStatus();
    }
  }, [user, fetchSubscriptionStatus]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    subscription,
    refreshSubscription
  }), [subscription, refreshSubscription]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
